import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Loading,
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { marketsForScope } from '../dataProvider/types';
import type {
  AdminDataProvider,
  CmsPageMarket,
  SliderSlide,
} from '../dataProvider';

/**
 * The slider slide authoring form (D-cms-sliders). A flat CRUD form: placement (a FREE
 * CMS-owned key), imageUrl (a URL, the image-deferral shape), the PLAIN { label, link }
 * CTA (a marketing slide links to a plain URL, NOT the app blocks' { kind, ref } link),
 * market and the active toggle. There is NO publish lifecycle (a slider is
 * active-and-ordered, like testimonials). A new slide is appended to the end of its
 * placement (order = max + 1). Authoring gates cms:author, operational market-scoped.
 */

const SliderFormInner = ({ record }: { record?: SliderSlide }) => {
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();
  const { identity } = useGetIdentity();
  const { activeRegion } = useRegion();

  const isEdit = record != null;
  const markets = useMemo(
    () => marketsForScope(activeRegion ?? 'GLOBAL'),
    [activeRegion],
  );
  // A slide targets a single in-scope market OR both (the RuleMarket idiom).
  const marketChoices: CmsPageMarket[] = [...markets, 'all'];

  const [placement, setPlacement] = useState(
    record?.placement ?? 'homepage-hero',
  );
  const [imageUrl, setImageUrl] = useState(record?.imageUrl ?? '');
  const [ctaLabel, setCtaLabel] = useState(record?.cta.label ?? '');
  const [ctaLink, setCtaLink] = useState(record?.cta.link ?? '');
  const [market, setMarket] = useState<CmsPageMarket>(
    record?.market ?? marketChoices[0] ?? 'uk',
  );
  const [active, setActive] = useState(record?.active ?? true);

  const validate = (): string | null => {
    if (!placement.trim()) return 'A placement is required';
    if (!imageUrl.trim()) return 'An image URL is required';
    if (!ctaLabel.trim()) return 'A CTA label is required';
    return null;
  };

  // The next order at the END of the placement (append), so a new slide does not collide.
  const nextOrder = async (place: string): Promise<number> => {
    const { data } = await dataProvider.getList('sliderSlides', {
      pagination: { page: 1, perPage: 500 },
      sort: { field: 'order', order: 'DESC' },
      filter: { placement: place },
    });
    const top = (data as SliderSlide[])[0];
    return top ? top.order + 1 : 0;
  };

  const save = async () => {
    const problem = validate();
    if (problem) {
      notify(problem, { type: 'error' });
      return;
    }
    const nowIso = new Date().toISOString();
    const cta = { label: ctaLabel, link: ctaLink };
    try {
      if (isEdit && record) {
        await dataProvider.update('sliderSlides', {
          id: record.id,
          data: {
            placement,
            imageUrl,
            cta,
            market,
            active,
            updatedAt: nowIso,
          },
          previousData: record,
        });
        notify('Slide saved', { type: 'success' });
        redirect('list', 'sliderSlides');
      } else {
        const order = await nextOrder(placement);
        await dataProvider.create('sliderSlides', {
          data: {
            placement,
            imageUrl,
            cta,
            order,
            market,
            active,
            createdBy: String(identity?.email ?? identity?.id ?? 'unknown'),
            createdAt: nowIso,
            updatedAt: nowIso,
          },
        });
        notify('Slide created', { type: 'success' });
        redirect('list', 'sliderSlides');
      }
    } catch {
      notify('Could not save the slide', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        {isEdit ? 'Edit slide' : 'New slide'}
      </Typography>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Placement"
              value={placement}
              onChange={(e) => setPlacement(e.target.value)}
              helperText="A marketing-site slot, e.g. homepage-hero"
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'slide-placement' }}
            />
            <TextField
              select
              label="Market"
              value={market}
              onChange={(e) => setMarket(e.target.value as CmsPageMarket)}
              margin="dense"
              size="small"
              sx={{ minWidth: 140 }}
              slotProps={{ select: { native: true } }}
              inputProps={{ 'data-testid': 'slide-market' }}
            >
              {marketChoices.map((m) => (
                <option key={m} value={m}>
                  {m === 'all' ? 'Both markets' : m.toUpperCase()}
                </option>
              ))}
            </TextField>
          </Stack>
          <TextField
            label="Image URL"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            helperText="A URL to the slide image (no upload)"
            margin="dense"
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'slide-image' }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="CTA label"
              value={ctaLabel}
              onChange={(e) => setCtaLabel(e.target.value)}
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'slide-cta-label' }}
            />
            <TextField
              label="CTA link"
              value={ctaLink}
              onChange={(e) => setCtaLink(e.target.value)}
              helperText="A plain URL"
              margin="dense"
              size="small"
              fullWidth
              inputProps={{ 'data-testid': 'slide-cta-link' }}
            />
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                data-testid="slide-active-switch"
              />
            }
            label="Active"
          />
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2}>
        <Button variant="contained" onClick={save} data-testid="slide-save">
          {isEdit ? 'Save' : 'Create slide'}
        </Button>
        <Button onClick={() => redirect('list', 'sliderSlides')}>Cancel</Button>
      </Stack>
    </Box>
  );
};

/** Create view. Exported for testing without the access wrapper. */
export const SliderCreateView = () => <SliderFormInner />;

/** Edit view. Exported for testing without the access wrapper. */
export const SliderEditView = () => {
  const { id } = useParams();
  const { data: record, isLoading } = useGetOne<SliderSlide>('sliderSlides', {
    id: Number(id),
  });
  if (isLoading || !record) return <Loading />;
  return <SliderFormInner record={record} />;
};

export const SliderCreate = () => (
  <RequireAccess capability="cms:author">
    <SliderCreateView />
  </RequireAccess>
);

export const SliderEdit = () => (
  <RequireAccess capability="cms:author">
    <SliderEditView />
  </RequireAccess>
);
